import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ImageMetadata } from '../../types/ui';
import { FILE_UPLOAD_STATUS } from '../../constants';

type FileUploadStatus =
  (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];

export type QueueFileData = {
  guid: string;
  file: File;
  status: FileUploadStatus;
  reasons: string[]; // reasons for failure, if any
};

export type GalleryReducerState = {
  uploadedImageDetails: ImageMetadata[]; //All images in image details
  uploadedImagesImageTypePhotos: ImageMetadata[]; //these are the images that retrieved from image detail that are of image type 5
  uploadQueue: QueueFileData[]; //these are the images that are pending,failed,reject user is t
  uploadedSuccessfullyCurrentSession: string[]; //these are the image guids that were uploaded successfully in current session
  cloneQueue: string[];
};

const initialState: GalleryReducerState = {
  uploadQueue: [],
  cloneQueue: [],
  uploadedImagesImageTypePhotos: [],
  uploadedSuccessfullyCurrentSession: [],
  uploadedImageDetails: [],
};

const gallerySlice = createSlice({
  name: 'gallery',
  initialState,
  reducers: {
    reset: () => initialState,

    setUploadedGalleryImages: (
      state: GalleryReducerState,
      action: PayloadAction<ImageMetadata[]>
    ) => {
      state.uploadedImagesImageTypePhotos = action.payload.filter(
        (img) => img.imageType === '5'
      );
      state.uploadedImageDetails = action.payload;
    },

    deleteUploadedGalleryImage: (
      state: GalleryReducerState,
      action: PayloadAction<ImageMetadata>
    ) => {
      state.uploadedImagesImageTypePhotos =
        state.uploadedImagesImageTypePhotos.filter(
          (img) => img.guid != action.payload.guid
        );
      //physically deleted from image details
      state.uploadedImageDetails = state.uploadedImageDetails.filter(
        (img) => img.guid != action.payload.guid
      );
      state.uploadedSuccessfullyCurrentSession =
        state.uploadedSuccessfullyCurrentSession.filter(
          (guid) => guid != action.payload.guid
        );
      const array = [...state.uploadQueue];
      const index = array.findIndex(
        (item) => item.guid === action.payload.guid
      );
      array.splice(index, 1);
      state.uploadQueue = array;
    },

    addToUploadQueue: (
      state: GalleryReducerState,
      action: PayloadAction<QueueFileData[]>
    ) => {
      const payload = action.payload;
      if (payload.length === 0) return;
      if (Array.isArray(payload)) {
        state.uploadQueue.push(...payload);
      }
    },
    removeRejectedUploadQueue: (state: GalleryReducerState) => {
      state.uploadQueue = state.uploadQueue.filter(
        (item) => item.status !== 'rejected'
      );
    },
    updateQueueMediaStatus: (
      state: GalleryReducerState,
      action: PayloadAction<{
        status: FileUploadStatus;
        guid: string;
        reasons: string[];
      }>
    ) => {
      const { status, guid, reasons } = action.payload;
      const array = [...state.uploadQueue];
      const index = array.findIndex((item) => item.guid === guid);
      if (index !== -1) {
        array[index].status = status;
        array[index].reasons = reasons;
        if (status === 'success') {
          state.uploadedSuccessfullyCurrentSession.push(guid);
          array.splice(index, 1);
        }
      }
      state.uploadQueue = array;
    },
    addToCloneQueue: (
      state: GalleryReducerState,
      action: PayloadAction<string>
    ) => {
      const labelName = action.payload;
      if (labelName.length === 0) return;
      state.cloneQueue.push(labelName);
    },
    removeFromCloneQueue: (
      state: GalleryReducerState,
      action: PayloadAction<string>
    ) => {
      const labelToRemove = action.payload;
      if (labelToRemove.length === 0) return;
      const array = [...state.cloneQueue];
      const others = array.filter((label) => label !== labelToRemove);
      state.cloneQueue = others;
    },
  },
});

export const {
  setUploadedGalleryImages,
  deleteUploadedGalleryImage,
  addToUploadQueue,
  removeRejectedUploadQueue,
  reset,
  updateQueueMediaStatus,
  addToCloneQueue,
  removeFromCloneQueue,
} = gallerySlice.actions;
export default gallerySlice.reducer;
